import { useEffect, useMemo, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Copy, X, Check, QrCode, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { NativeShare } from "@/components/NativeShare";

/**
 * Modal de compartir catálogo — QR grande para escanear en vivo.
 *
 * UX: el QR es el FOCO. Está pensado para que el vendedor levante el
 * teléfono en un local/evento y otra persona lo escanee con su cámara.
 * No hay descargas — un QR permanente permite guardar screenshot si el
 * usuario quiere imprimirlo después.
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - sellerName: string
 *   - slug: string
 *   - fallbackUrl?: string (cuentas sin slug — usa /vendedor/<UUID>)
 */
export default function ShareCatalogModal({ open, onClose, sellerName, slug, fallbackUrl }) {
  const [copied, setCopied] = useState(false);

  const url = useMemo(() => {
    if (!open) return "";
    const origin = window.location.origin;
    if (slug) return `${origin}/tienda/${slug}`;
    if (fallbackUrl) return fallbackUrl.startsWith("http") ? fallbackUrl : `${origin}${fallbackUrl}`;
    return origin;
  }, [open, slug, fallbackUrl]);

  const shortUrl = useMemo(() => {
    if (!url) return "";
    try {
      const u = new URL(url);
      return `${u.host}${u.pathname}`;
    } catch { return url; }
  }, [url]);

  // Cerrar con Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Enlace copiado");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("No se pudo copiar. Selecciona el enlace y copialo manualmente.");
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="share-catalog-modal-overlay"
    >
      <div
        className="bg-[#0A0A0A] border border-[#262626] rounded-md max-w-sm w-full my-4"
        onClick={(e) => e.stopPropagation()}
        data-testid="share-catalog-modal"
      >
        <div className="flex items-center justify-between p-4 border-b border-[#262626]">
          <div className="flex items-center gap-2">
            <QrCode size={18} className="text-[#EF4444]" />
            <div className="font-display text-base sm:text-lg">Escanea mi catálogo</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-500 hover:text-white"
            data-testid="share-catalog-close"
            aria-label="Cerrar"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-4">
          {/* QR — el foco del modal. Blanco puro, sin frame, nítido, grande. */}
          <div
            className="bg-white rounded-md mx-auto flex items-center justify-center"
            style={{ padding: "20px", width: "fit-content" }}
            data-testid="share-qr-wrapper"
          >
            <QRCodeCanvas
              value={url}
              size={280}
              bgColor="#FFFFFF"
              fgColor="#000000"
              level="H"
              includeMargin={false}
            />
          </div>

          <div className="text-center text-xs text-neutral-400 leading-relaxed">
            Apunta con la cámara de otro teléfono para abrir tu catálogo.
          </div>

          {/* Enlace corto + copiar */}
          <div className="space-y-2">
            <NativeShare title={sellerName ? `Catálogo de ${sellerName} en HitTCG` : "Mi catálogo en HitTCG"}
                         text={sellerName ? `Mira el catálogo de ${sellerName} en HitTCG` : "Mira mi catálogo en HitTCG"}
                         url={url} testid="share-catalog-native" />
            <div className="text-[10px] uppercase tracking-wider text-neutral-500">Enlace</div>
            <div className="flex items-center gap-2">
              <div
                className="flex-1 min-w-0 bg-[#111] border border-[#262626] rounded px-3 py-2 text-xs sm:text-sm text-white truncate select-all font-mono"
                data-testid="share-catalog-url"
                title={url}
              >
                {shortUrl}
              </div>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-neutral-500 hover:text-white shrink-0"
                aria-label="Abrir en nueva pestaña"
                data-testid="share-catalog-open"
              >
                <ExternalLink size={16} />
              </a>
            </div>
            <Button
              type="button"
              onClick={doCopy}
              className="w-full bg-[#EF4444] hover:bg-[#DC2626]"
              data-testid="share-catalog-copy-btn"
            >
              {copied ? <Check size={16} className="mr-2" /> : <Copy size={16} className="mr-2" />}
              {copied ? "Copiado" : "Copiar enlace"}
            </Button>
          </div>

          <div className="text-[11px] text-neutral-500 leading-relaxed text-center">
            Este enlace es <b className="text-neutral-300">permanente</b>. Si personalizas tu slug, los enlaces
            viejos siguen redirigiendo automáticamente al nuevo.
          </div>
        </div>
      </div>
    </div>
  );
}
